// Card penalties — single source of truth, used by Rules, GameSetup, the
// Scorecard, and the penalty modal so they never drift out of sync.

export const YELLOW = '#F5C542';
export const RED = '#D94F4F';

export const YELLOW_CARD = {
  type: 'yellow',
  label: 'Yellow Card',
  strokes: 1,
  description: '+1 stroke',
  blurb:
    'A formal warning. Issued by the Caddy for minor breaches of decorum on the course.',
  infractions: [
    'Use of the lavatory at a Water Hazard hole',
    "Spillage of one's beverage, including dribbling",
    'Loss of vertical stability (falling)',
    'Excessive slurring of speech',
    "Disputing the Caddy's authority",
    "Unreasonable delay in consuming one's drink",
  ],
};

export const RED_CARD = {
  type: 'red',
  label: 'Red Card',
  strokes: 4,
  description: '+4 strokes',
  blurb:
    'A serious foul. Issued by the Caddy when the spirit of the round has been broken.',
  infractions: [
    'Regurgitation of any kind',
    'Ejection from an establishment',
    'Physical altercation',
  ],
};

export const ALL_CARDS = [YELLOW_CARD, RED_CARD];

export const CARDS_BY_TYPE = {
  yellow: YELLOW_CARD,
  red: RED_CARD,
};

// ===== Helpers =====

/** Make a fresh per-player card record. */
export function newCardRecord() {
  return {
    yellowCards: 0, // RAW count of yellows ever issued (incl. ones that converted)
    autoReds: 0, // count of yellow→red conversions (every 2nd yellow)
    straightReds: 0, // direct red cards
    cardLog: [], // audit log: [{type, reason, hole, at}]
  };
}

/**
 * Migrate legacy `{ yellow, red }` to the new shape, or return as-is.
 */
export function normalizeCardRecord(cards) {
  if (!cards || typeof cards !== 'object') return newCardRecord();
  if ('yellowCards' in cards) {
    return {
      yellowCards: Number(cards.yellowCards) || 0,
      autoReds: Number(cards.autoReds) || 0,
      straightReds: Number(cards.straightReds) || 0,
      cardLog: Array.isArray(cards.cardLog) ? cards.cardLog : [],
    };
  }
  // legacy { yellow, red }
  const yc = Number(cards.yellow) || 0;
  return {
    yellowCards: yc,
    autoReds: Math.floor(yc / 2),
    straightReds: Number(cards.red) || 0,
    cardLog: [],
  };
}

/**
 * Issue a yellow card. The 2nd, 4th, ... yellow auto-converts to a red.
 * Returns a new card record (immutable).
 *
 * Stroke math:
 *   1st yellow            → +1 stroke
 *   2nd yellow (auto-red) → +2 strokes  (so the pair is worth +3 total)
 *   straight red          → +4 strokes
 */
export function issueYellow(cards, { reason, hole } = {}) {
  const next = {
    ...cards,
    yellowCards: (cards.yellowCards || 0) + 1,
    autoReds: cards.autoReds || 0,
    straightReds: cards.straightReds || 0,
    cardLog: [...(cards.cardLog || [])],
  };
  // Every even yellow (2, 4, 6...) auto-converts.
  const triggeredAutoRed = next.yellowCards % 2 === 0;
  if (triggeredAutoRed) next.autoReds += 1;
  next.cardLog.push({
    type: 'yellow',
    autoRed: triggeredAutoRed,
    reason: reason || null,
    hole: typeof hole === 'number' ? hole : null,
    at: Date.now(),
  });
  return next;
}

/** Issue a straight red card. Returns a new card record (immutable). */
export function issueRed(cards, { reason, hole } = {}) {
  const next = {
    yellowCards: cards.yellowCards || 0,
    autoReds: cards.autoReds || 0,
    straightReds: (cards.straightReds || 0) + 1,
    cardLog: [...(cards.cardLog || []), {
      type: 'red',
      reason: reason || null,
      hole: typeof hole === 'number' ? hole : null,
      at: Date.now(),
    }],
  };
  return next;
}

/**
 * Total penalty strokes for a player's card record.
 *
 *   yellows that haven't converted → +1 each
 *   each auto-red (the 2nd of a pair) → +2 extra
 *   each straight red → +4
 *
 * Equivalent: yellowCards + autoReds + (straightReds * 4)
 *
 *   2 yellows = 2 + 1 + 0 = 3 ✓
 *   3 yellows = 3 + 1 + 0 = 4 ✓ (one converted pair + one loose yellow)
 *   4 yellows = 4 + 2 + 0 = 6 ✓ (two converted pairs)
 *   1 straight red = 0 + 0 + 4 = 4 ✓
 */
export function computePenaltyTotal(cards) {
  if (!cards) return 0;
  const yc = Number(cards.yellowCards) || 0;
  const ar = Number(cards.autoReds) || 0;
  const sr = Number(cards.straightReds) || 0;
  return yc + ar + sr * 4;
}

/**
 * Compute the total RED card count (auto + straight) — useful for the
 * scorecard badge display.
 */
export function totalRedCards(cards) {
  if (!cards) return 0;
  return (Number(cards.autoReds) || 0) + (Number(cards.straightReds) || 0);
}

/**
 * Yellow cards still showing as yellow (i.e. not yet paired into a red).
 */
export function visibleYellowCards(cards) {
  if (!cards) return 0;
  const yc = Number(cards.yellowCards) || 0;
  const ar = Number(cards.autoReds) || 0;
  return yc - ar * 2;
}
